import { Card, CardContent } from '../ui/card';
import { CPDStatusBadge, ProgressRing, Bar } from './CPDStatus';
import { formatAUDate } from '../../lib/dateUtils';
import { Info, ArrowRightLeft } from 'lucide-react';

const YearBars = ({ r }) => (
  <div className="space-y-2">
    {r.years.filter((y) => y.state !== 'future').map((y) => (
      <Bar key={y.start} label={`CPD year ${formatAUDate(y.start)} – ${formatAUDate(y.end)}${y.state === 'current' ? ' (current)' : ''}`}
        value={y.hours} max={y.required || null} sub={y.state === 'current' ? `${y.days_remaining} days left` : 'completed'} />
    ))}
  </div>
);

const VerifiedSplit = ({ r }) => (
  <div className="grid grid-cols-2 gap-3 text-xs">
    <div className="rounded-lg border p-2"><p className="text-muted-foreground">Verified / verifiable</p>
      <p className="text-base font-semibold" data-testid={`cpd-verified-${r.user_framework_id}`}>{r.verifiable_hours} h{r.verified_min ? ` / ${r.verified_min}` : ''}</p></div>
    <div className="rounded-lg border p-2"><p className="text-muted-foreground">Unverified</p>
      <p className="text-base font-semibold">{r.non_verifiable_hours} h{r.unverified_min ? ` / ${r.unverified_min}` : ''}{r.unverified_max != null ? ` (max ${r.unverified_max})` : ''}</p></div>
  </div>
);

export const CPDFrameworkCard = ({ r }) => (
  <Card data-testid={`cpd-fw-card-${r.user_framework_id}`} className="overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500">
    <CardContent className="pt-6 space-y-5">
      <div className="flex items-start gap-5">
        <ProgressRing value={r.counted} max={r.required} status={r.status} />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{r.framework_name}</h3>
            <CPDStatusBadge status={r.status} testId={`cpd-status-${r.user_framework_id}`} />
          </div>
          <p className="text-xs text-muted-foreground">
            Period {formatAUDate(r.period_start)} – {formatAUDate(r.period_end)} · {r.days_remaining_period} days left · rules v{r.version}
            {r.member_category ? ` · ${r.member_category}` : ''}
          </p>
          <ul className="text-sm space-y-0.5" data-testid={`cpd-reasons-${r.user_framework_id}`}>
            {r.reasons.slice(0, 4).map((x) => <li key={x} className="text-foreground/80">• {x}</li>)}
          </ul>
          {r.provisional_hours > 0 && <p className="text-xs text-muted-foreground">{r.provisional_hours} h awaiting approval (not counted yet)</p>}
        </div>
      </div>
      {r.status !== 'NOT_APPLICABLE' && (
        <>
          <VerifiedSplit r={r} />
          {r.annual_min > 0 && <YearBars r={r} />}
          {r.areas?.length > 0 && (
            <div className="space-y-2" data-testid={`cpd-areas-${r.user_framework_id}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Competency areas</p>
              {r.areas.map((a) => <Bar key={a.code} label={a.name} value={a.hours} max={a.min} sub={`${a.verified} verified / ${a.unverified} unverified (min ${a.verified_min}/${a.unverified_min})`} />)}
            </div>
          )}
          {r.categories.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Categories & caps</p>
              {r.categories.map((c, i) => (
                <Bar key={i} label={`${c.category}${c.verifiable_only ? ' (verifiable)' : ''}${c.scope === 'year' ? ' – per year' : ''}`}
                  value={c.hours} max={c.min || c.cap} sub={c.cap != null ? `cap ${c.cap} h (${c.cap_used_pct ?? 0}% used)` : c.min ? 'minimum' : ''} />
              ))}
            </div>
          )}
          {r.type_caps?.length > 0 && (
            <div className="text-xs space-y-1">{r.type_caps.map((t) => <p key={t.activity_type}>{t.activity_type}: {t.counted}/{t.hours} h counted (cap {t.cap} h)</p>)}</div>
          )}
          {r.carry_forward_calc && (
            <div className="flex gap-2 rounded-lg bg-slate-50 border p-3 text-xs" data-testid={`cpd-carry-${r.user_framework_id}`}>
              <ArrowRightLeft className="w-4 h-4 shrink-0 text-[#0055FF]" />
              <span><b>Carried forward</b> from {r.carry_forward_calc.from}: {r.carry_forward_calc.verified ?? 0} verified + {r.carry_forward_calc.unverified ?? 0} unverified h
                {r.carry_forward_calc.note ? ` – ${r.carry_forward_calc.note}` : ''}</span>
            </div>
          )}
          {r.excess_not_counted > 0 && <p className="text-xs text-amber-800">{r.excess_not_counted} h over caps/limits not counted.</p>}
        </>
      )}
      {r.info_notes?.length > 0 && (
        <details className="text-xs text-muted-foreground"><summary className="cursor-pointer flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Framework notes</summary>
          <ul className="mt-2 space-y-1">{r.info_notes.map((n) => <li key={n}>• {n}</li>)}</ul></details>
      )}
    </CardContent>
  </Card>
);
